import {
  DashboardState,
  EScreenType,
  EUserAccessPart,
  IDisplayItem,
  IProject,
  IScreenDesignInfo
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import jsonStringifySafe from "json-stringify-safe";
import _ from "lodash";
import * as React from "react";
import type {
  IScreenDesignExportData,
  ISdExportUsedStylings
} from "../../components/design-tools/header/toolbar-right/save";
import type { KuikaDropdownMenuItemType } from "../../components/ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import type { KuikaListTableRowAction } from "../../components/ui-kit/kuika-list-table/kuika-list-table";
import KuikaSvg from "../../components/ui-kit/kuika-svg/kuika-svg";
import KuikaText from "../../components/ui-kit/typography/Text/Text";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import { IIdName } from "../../models/interface/id-name";
import { ScreenService } from "../../services/screen-service";
import { getSdInstance } from "./designer-context";
import { duplicateDesignerScreen } from "./designer-screen-actions";
import { KMainFunctions } from "./main-functions";

/**
 * Per-screen row actions (rename / duplicate / export / delete) shared by every surface that
 * lists screens: the screens left panel (`screens-window.tsx`) and the row-2 screen selector
 * (`header/sub-bar/sub-bar-screen-selector.tsx`).
 *
 * Only the *flows* live here — each surface keeps its own UI state (which row is being renamed,
 * which delete modal is open) and passes it back in through the handlers. This is deliberately
 * not a hook/HOC: both callers are class components with different render shapes, and the thing
 * worth sharing is the behaviour, not the markup.
 */

export type DesignType = "screen" | "report";

/** Domain-access quota bucket for screens. */
const SCREEN_ACCESS_NAME = "Screens";

const SCREEN_NAME_RULES = /^[A-Za-z][A-Za-z0-9 ]+$/;

export interface IScreenActionAccess {
  canEditScreen: boolean;
  canDeleteScreen: boolean;
  canCreateScreen: boolean;
}

export const getScreenTypeOf = (screen: IScreenDesignInfo): EScreenType =>
  screen.publicMetaData?.screenType ?? EScreenType.Default;

export const getScreenActionAccess = (sd: IScreenDesignContextState): IScreenActionAccess => ({
  canEditScreen: KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
    EUserAccessPart.EditScreen,
    sd.project?.projectAccess
  ),
  canDeleteScreen: KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
    EUserAccessPart.DeleteScreen,
    sd.project?.projectAccess
  ),
  canCreateScreen: KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
    EUserAccessPart.CreateScreen,
    sd.project?.projectAccess
  )
});

/** Screens that count against the "Screens" domain quota — reports have their own. */
const countQuotaScreens = (sd: IScreenDesignContextState): number =>
  (sd.screenList || []).filter((screen: IScreenDesignInfo) => getScreenTypeOf(screen) !== EScreenType.Report).length;

// ---------------------------------------------------------------------------
// Rename
// ---------------------------------------------------------------------------

/**
 * `rejected` — the name was refused (a warning notification has already been shown) and the
 * caller must keep its rename editor open. `completed` — the rename was attempted; the caller
 * closes the editor either way, matching the panel's original try/finally.
 */
/**
 * Labels and glyphs for the four screen row actions. Both builders below read them, so the
 * dropdown menu (left panel, row-2 selector) and the list-table menu (Report Designer landing)
 * cannot end up calling the same action two different things.
 */
const SCREEN_ROW_ACTION_LABEL = {
  rename: "Rename",
  duplicate: "Duplicate",
  export: "Export Kuika File",
  delete: "Delete"
} as const;

const SCREEN_ROW_ACTION_ICON = {
  rename: "edit",
  duplicate: "copy-plus",
  export: "download",
  delete: "trash"
} as const;

export type ScreenRenameOutcome = "rejected" | "completed";

/**
 * Renames the screen currently open on the canvas. Only the current screen can be renamed:
 * the name is persisted by `saveScreen`, which always writes the loaded screen — which is why
 * the menu's Rename item is gated on `isCurrentScreen`.
 */
export const renameCurrentScreen = async (
  sd: IScreenDesignContextState,
  rawName: string
): Promise<ScreenRenameOutcome> => {
  const newName: string = (rawName ?? "").trim();
  const isScreenNameValid: boolean = SCREEN_NAME_RULES.test(newName);
  const isEqualToDsName = sd?.datasources?.find((ds) => ds.name.toLowerCase() === newName.toLowerCase());
  const isEqualToWfName = sd?.workFlowList?.find((wf) => wf.name.toLowerCase() === newName.toLowerCase());
  const isEqualToTableName = Array.from(sd?.schemas?.values() ?? []).find((schema) =>
    schema?.tables.find((table) => table.name.toLowerCase() === newName.toLowerCase())
  );
  const isEqualToScreenName = sd?.screenList?.find(
    (s: IScreenDesignInfo) => s.name.toLowerCase() === newName.toLowerCase() && s.id !== sd?.sdId
  );

  if (!isScreenNameValid) {
    KMainFunctions.displayWarningNotification("Screen Name is invalid");
    return "rejected";
  }
  if (isEqualToDsName || isEqualToWfName || isEqualToTableName) {
    KMainFunctions.displayWarningNotification("Screen name can not be equal to the datasource, table or process name.");
    return "rejected";
  }
  if (isEqualToScreenName) {
    KMainFunctions.displayWarningNotification("Screen name already exists");
    return "rejected";
  }

  try {
    if (newName.length > 0) {
      await sd.saveScreen(true, newName);
      sd.setIsScreenWindowReload(true);
    }
  } catch (error) {
    KMainFunctions.exceptionHandler(error);
  }
  return "completed";
};

// ---------------------------------------------------------------------------
// Duplicate
// ---------------------------------------------------------------------------

export interface IDuplicateScreenOptions {
  /**
   * Whether a successful duplicate closes the Report Designer's create page. True for the
   * left panel, which duplicates FROM that page and wants the result revealed; false for the
   * landing page itself, which duplicates from its own table and must stay on screen — the
   * new row appears in it.
   */
  closeCreatePage?: boolean;
  /** Runs after a successful duplicate, for callers that own their own list refresh. */
  onCompleted?: () => void;
}

/**
 * `onDomainAccessRequired` is the redux `showDomainAccessRequiredModal` dispatch — passed in
 * rather than dispatched here so this module stays free of store wiring.
 */
export const duplicateScreenFromRow = (
  sd: IScreenDesignContextState,
  screenId: string,
  screenName: string,
  onDomainAccessRequired: (accessName: string) => void,
  options: IDuplicateScreenOptions = {}
): void => {
  if (KMainFunctions.isDomainHasAccess(SCREEN_ACCESS_NAME, countQuotaScreens(sd)) === false) {
    onDomainAccessRequired(SCREEN_ACCESS_NAME);
    return;
  }

  sd.processWithSpinner(async () => {
    try {
      const result = await duplicateDesignerScreen(getSdInstance(), screenId);
      if ("error" in result) {
        throw new Error(result.error);
      }

      if (result.success) {
        KMainFunctions.displaySuccessNotification(`${screenName} screen has been duplicated successfully.`);
        await sd.setIsScreenWindowReload(true);
        if (options.closeCreatePage !== false) {
          sd.setVisibilityOfCreateReportDesignPage(false);
        }
        options.onCompleted?.();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  });
};

// ---------------------------------------------------------------------------
// Export Kuika file
// ---------------------------------------------------------------------------

const getUsedStylings = (
  usedStylings: ISdExportUsedStylings,
  displayItems: IDisplayItem[],
  project: IProject
): ISdExportUsedStylings => {
  displayItems.forEach((di: IDisplayItem) => {
    const borderIds = di.styleObj?.border?.borderId;
    if (borderIds !== undefined) {
      const borderIdList = borderIds?.split("|");
      if (borderIdList !== undefined) {
        borderIdList.forEach((borderId: string) => {
          if (borderId && Guid.isGuid(borderId) && !usedStylings.stylingBorder.find((x) => x.borderId === borderId)) {
            const data = project.stylingBorder.filter((x) => x.borderId === borderId);
            if (data && data.length !== 0) {
              usedStylings.stylingBorder.push(data[0]);
            }
          }
        });
      }
    }

    const shadowIds = di.styleObj?.shadow?.shadowId;
    if (shadowIds !== undefined) {
      const shadowIdList = shadowIds?.split("|");
      if (shadowIdList !== undefined) {
        shadowIdList.forEach((shadowId: string) => {
          if (shadowId && Guid.isGuid(shadowId) && !usedStylings.stylingShadow.find((x) => x.shadowId === shadowId)) {
            const data = project.stylingShadow.filter((x) => x.shadowId === shadowId);
            if (data && data.length !== 0) {
              usedStylings.stylingShadow.push(data[0]);
            }
          }
        });
      }
    }

    const posIds = di.styleObj?.position?.positionId;
    if (posIds !== undefined) {
      const posIdList = posIds?.split("|");
      if (posIdList !== undefined) {
        posIdList.forEach((posId: string) => {
          if (posId && Guid.isGuid(posId) && !usedStylings.stylingPosition.find((x) => x.positionId === posId)) {
            const data = project.stylingPosition.filter((x) => x.positionId === posId);
            if (data && data.length !== 0) {
              usedStylings.stylingPosition.push(data[0]);
            }
          }
        });
      }
    }

    const typoIds = di.styleObj?.text?.typoId;
    if (typoIds !== undefined) {
      const typoIdList = typoIds?.split("|");
      if (typoIdList !== undefined) {
        typoIdList.forEach((typoId: string) => {
          if (typoId && Guid.isGuid(typoId) && !usedStylings.stylingTypography.find((x) => x.typoId === typoId)) {
            const data = project.stylingTypography.filter((x) => x.typoId === typoId);
            if (data && data.length !== 0) {
              usedStylings.stylingTypography.push(data[0]);
            }
          }
        });
      }
    }

    const fillIds = di.styleObj?.fill?.fillId;
    if (fillIds !== undefined) {
      const fillIdList = fillIds?.split("|");
      if (fillIdList !== undefined) {
        fillIdList.forEach((fillId: string) => {
          if (fillId && Guid.isGuid(fillId) && !usedStylings.stylingFill?.find((x) => x.fillId === fillId)) {
            const data = project.stylingFill?.filter((x) => x.fillId === fillId);
            if (data && data.length !== 0) {
              usedStylings.stylingFill?.push(data[0]);
            }
          }
        });
      }
    }

    const effectsIds = di.styleObj?.effects?.effectsId;
    if (effectsIds !== undefined && effectsIds !== null) {
      const effectsIdList = effectsIds?.split("|");
      if (effectsIdList !== undefined) {
        effectsIdList.forEach((effectsId: string) => {
          if (
            effectsId &&
            Guid.isGuid(effectsId) &&
            !usedStylings.stylingEffects?.find((x) => x.effectsId === effectsId)
          ) {
            const data = project.stylingEffects?.filter((x) => x.effectsId === effectsId);
            if (data && data.length !== 0) {
              usedStylings.stylingEffects?.push(data[0]);
            }
          }
        });
      }
    }

    usedStylings = getUsedStylings(usedStylings, di.children, project);
  });

  return usedStylings;
};

const getExportKuikaFileComponentsRecursive = (
  sd: IScreenDesignContextState,
  displayItems: IDisplayItem[],
  components: IIdName[]
): IIdName[] => {
  displayItems.forEach((di: IDisplayItem) => {
    if (di.item.id.length === 36) {
      const comp = sd.project.componentSetObj.components.find((x) => x.id === di.item.id);
      if (comp && components.find((x) => x.id === comp?.id) === undefined) {
        components.push({ id: di.item.id, name: comp.name } as IIdName);
      }
    }
    components = getExportKuikaFileComponentsRecursive(sd, di.children, components);
  });
  return components;
};

/** Downloads the current screen's design as a `*_exportfile.json` (Kuika-developer-only action). */
export const exportScreenKuikaFile = (sd: IScreenDesignContextState): void => {
  const exportObj = {} as IScreenDesignExportData;
  const screenDesign = sd.getScreenDesign();
  const displayItems: IDisplayItem[] = _.cloneDeep(screenDesign.displayItems);

  let components: IIdName[] = [];
  let usedStylings = {
    stylingTypography: [],
    stylingBorder: [],
    stylingShadow: [],
    stylingPosition: [],
    stylingFill: [],
    stylingEffects: []
  } as ISdExportUsedStylings;

  usedStylings = getUsedStylings(usedStylings, screenDesign.displayItems, sd.project);
  components = getExportKuikaFileComponentsRecursive(sd, displayItems, components);

  exportObj.screenName = sd.sdName;
  exportObj.componentsUsedInScreen = components;
  exportObj.usedStylings = usedStylings;
  exportObj.displayItems = displayItems;
  exportObj.screenInputs = _.cloneDeep(sd.getScreenInputs());
  exportObj.screenOutputs = _.cloneDeep(sd.getScreenOutputs());

  KMainFunctions.exportFile(jsonStringifySafe(exportObj), `${screenDesign.name}_exportfile.json`);
};

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

/**
 * Guard in front of the delete modal: a project must keep at least one screen. Warns and
 * returns false when the row is the last one.
 */
export const canOpenDeleteScreenModal = (sd: IScreenDesignContextState): boolean => {
  if (sd.screenList.length === 1) {
    KMainFunctions.displayWarningNotification("You can't delete last screen of project.");
    return false;
  }
  return true;
};

/**
 * Runs the confirmed delete. `onAfterDelete` is the caller's own refresh (the panel reloads its
 * recently-opened list); it always runs, success or failure, mirroring the original `finally`.
 */
export const deleteScreenFromRow = async (
  sd: IScreenDesignContextState,
  screenToDelete: IScreenDesignInfo,
  designType: DesignType,
  onAfterDelete?: () => void
): Promise<void> => {
  const reportDesignPageToOpen: IScreenDesignInfo | undefined = sd.screenList?.find(
    (screen: IScreenDesignInfo) => getScreenTypeOf(screen) === EScreenType.Report && screen.id !== screenToDelete.id
  );
  const screenToOpen: IScreenDesignInfo | undefined = sd.screenList?.find(
    (screen: IScreenDesignInfo) => screen.id !== screenToDelete.id && getScreenTypeOf(screen) !== EScreenType.Report
  );

  const project: IProject = sd.getProject();
  const projectConfig = project?.processStepScreenIds?.find((pss) => pss.screenId == screenToDelete.id);
  if (projectConfig) {
    project.processStepScreenIds = project.processStepScreenIds.filter((item) => item.screenId !== screenToDelete.id);
    // KOA-18199: awaited on purpose — this prune used to be fire-and-forget, so a
    // designer-live project refresh could land first and resurrect the stale mapping.
    await sd.updateProject(project);
  }
  if (designType === "screen" && !screenToOpen) {
    KMainFunctions.displayWarningNotification("You can't delete last screen of project.");
    return;
  }
  const deletedScreenId: string = screenToDelete.id?.toString();
  try {
    await sd.saveScreen(true);
    if (sd.sdId === screenToDelete.id) {
      const res = await ScreenService.DeleteCurrentScreen(screenToDelete.id.toString());
      if (res.status === 200) {
        KMainFunctions.displaySuccessNotification(
          `${screenToDelete.name} ${designType} has been deleted successfully.`
        );
        sd.setIsScreenWindowReload(true);
        getSdInstance().startProcessTriggerSync.pruneScreen(deletedScreenId);
        if (designType === "report") {
          if (reportDesignPageToOpen) {
            sd.loadScreen(reportDesignPageToOpen.id);
          } else {
            sd.loadScreen(screenToOpen?.id);
            sd.setVisibilityOfCreateReportDesignPage(true);
            sd.setDashboardState(DashboardState.design);
          }
        } else {
          sd.loadScreen(screenToOpen?.id);
        }
      }
    } else {
      const res = await ScreenService.Delete(screenToDelete.id.toString());
      if (res.status === 200) {
        KMainFunctions.displaySuccessNotification(
          `${screenToDelete.name} ${designType} has been deleted successfully.`
        );
        sd.setIsScreenWindowReload(true);
        getSdInstance().startProcessTriggerSync.pruneScreen(deletedScreenId);
        const masterId: string | undefined = sd.getMasterScreenId(sd.screenList);
        if (masterId === screenToDelete.id) {
          sd.loadScreen(sd.sdId, true);
        }
      }
    }
  } catch (error) {
    KMainFunctions.exceptionHandler(error);
  } finally {
    onAfterDelete?.();
  }
};

// ---------------------------------------------------------------------------
// Menu
// ---------------------------------------------------------------------------

export interface IScreenActionMenuHandlers {
  /** Open the caller's inline rename editor for this screen. */
  onRename: (screenId: string) => void;
  onDuplicate: (screen: IScreenDesignInfo) => void;
  onExport: () => void;
  /** Open the caller's delete-confirmation modal for this screen. */
  onDelete: (screen: IScreenDesignInfo) => void;
}

export interface IScreenActionMenuParams {
  sd: IScreenDesignContextState;
  screen: IScreenDesignInfo;
  /** Rename and export only apply to the screen loaded on the canvas. */
  isCurrentScreen: boolean;
  /** `context.userInfo.username` — export is a Kuika-developer-only action. */
  username: string | undefined;
  handlers: IScreenActionMenuHandlers;
}

/**
 * The "…" menu for one screen row. Identical on every surface, so both the panel and the row-2
 * selector render exactly the same items, icons and test ids.
 */
export const buildScreenActionMenu = (params: IScreenActionMenuParams): KuikaDropdownMenuItemType[] => {
  const { sd, screen, isCurrentScreen, username, handlers } = params;
  const access: IScreenActionAccess = getScreenActionAccess(sd);

  const menu: (KuikaDropdownMenuItemType | false | undefined)[] = [
    access.canEditScreen &&
      isCurrentScreen && {
        label: SCREEN_ROW_ACTION_LABEL.rename,
        icon: (
          <KuikaSvg
            id={`screenRenameIcon-${screen.id}`}
            data-onboarding-id="left-panel_screens_rename-screen"
            width={16}
            height={16}
            name={SCREEN_ROW_ACTION_ICON.rename}
          />
        ),
        onClick: () => handlers.onRename(screen.id)
      },
    access.canCreateScreen && {
      label: SCREEN_ROW_ACTION_LABEL.duplicate,
      icon: (
        <KuikaSvg
          id={`screenDuplicateIcon-${screen.id}`}
          data-testid="duplicateButton"
          data-onboarding-id="left-panel_screens_duplicate-screen"
          width={16}
          height={16}
          name={SCREEN_ROW_ACTION_ICON.duplicate}
        />
      ),
      onClick: () => handlers.onDuplicate(screen)
    },
    KMainFunctions.isUserKuikaDeveloper(username ?? "") &&
      isCurrentScreen && {
        label: SCREEN_ROW_ACTION_LABEL.export,
        icon: (
          <KuikaSvg
            id={`exportKuikaFileIcon-${screen.id}`}
            data-testid="exportKuikaFileButton"
            width={16}
            height={16}
            name={SCREEN_ROW_ACTION_ICON.export}
          />
        ),
        onClick: () => handlers.onExport()
      },
    access.canDeleteScreen && {
      label: <KuikaText data-testid="ScreenDeleteButton">{SCREEN_ROW_ACTION_LABEL.delete}</KuikaText>,
      icon: (
        <KuikaSvg id={`screenDeleteIcon-${screen.id}`} width={16} height={16} name={SCREEN_ROW_ACTION_ICON.delete} />
      ),
      onClick: () => handlers.onDelete(screen),
      danger: true
    }
  ];

  return menu.filter((item): item is KuikaDropdownMenuItemType => Boolean(item));
};

/** True when the row's "…" menu would have at least one item. */
export const hasAnyScreenRowAction = (sd: IScreenDesignContextState): boolean => {
  const access: IScreenActionAccess = getScreenActionAccess(sd);
  return access.canEditScreen || access.canDeleteScreen || access.canCreateScreen;
};

export interface IScreenRowActionsParams {
  sd: IScreenDesignContextState;
  /** `context.userInfo.username` — export is a Kuika-developer-only action. */
  username: string | undefined;
  /** Rename and export only apply to the screen loaded on the canvas. */
  isCurrentScreen: (screen: IScreenDesignInfo) => boolean;
  handlers: IScreenActionMenuHandlers;
}

/**
 * The same four actions as `buildScreenActionMenu`, shaped for `KuikaListTable`'s row menu —
 * for a surface that lists screens as a TABLE (the Report Designer landing page) rather than as
 * a panel tree. Access rules, labels and glyphs are the ones above; what changes is where
 * per-row eligibility lives: the table builds its actions once and asks `hidden` per row, so
 * "only the open screen can be renamed" becomes a predicate instead of a build-time flag.
 */
export const buildScreenRowActions = (
  params: IScreenRowActionsParams
): KuikaListTableRowAction<IScreenDesignInfo>[] => {
  const { sd, username, isCurrentScreen, handlers } = params;
  const access: IScreenActionAccess = getScreenActionAccess(sd);

  const actions: (KuikaListTableRowAction<IScreenDesignInfo> | false)[] = [
    access.canEditScreen && {
      key: "rename",
      label: SCREEN_ROW_ACTION_LABEL.rename,
      icon: <KuikaSvg width={16} height={16} name={SCREEN_ROW_ACTION_ICON.rename} />,
      hidden: (screen: IScreenDesignInfo): boolean => !isCurrentScreen(screen),
      onSelect: (screen: IScreenDesignInfo): void => handlers.onRename(screen.id)
    },
    access.canCreateScreen && {
      key: "duplicate",
      label: SCREEN_ROW_ACTION_LABEL.duplicate,
      icon: <KuikaSvg width={16} height={16} name={SCREEN_ROW_ACTION_ICON.duplicate} />,
      onSelect: (screen: IScreenDesignInfo): void => handlers.onDuplicate(screen)
    },
    KMainFunctions.isUserKuikaDeveloper(username ?? "") && {
      key: "export",
      label: SCREEN_ROW_ACTION_LABEL.export,
      icon: <KuikaSvg width={16} height={16} name={SCREEN_ROW_ACTION_ICON.export} />,
      hidden: (screen: IScreenDesignInfo): boolean => !isCurrentScreen(screen),
      onSelect: (): void => handlers.onExport()
    },
    access.canDeleteScreen && {
      key: "delete",
      label: SCREEN_ROW_ACTION_LABEL.delete,
      icon: <KuikaSvg width={16} height={16} name={SCREEN_ROW_ACTION_ICON.delete} />,
      danger: true,
      onSelect: (screen: IScreenDesignInfo): void => handlers.onDelete(screen)
    }
  ];

  return actions.filter((action): action is KuikaListTableRowAction<IScreenDesignInfo> => Boolean(action));
};
